// Single source of truth for translating raw stream events (live OR
// persisted) into ThinkingEntry rows. The live ask() loop in Chat.tsx
// and the saved-session hydrator both run through this so the panel
// looks identical whether you just ran a turn or you're reopening one
// from last week.

import type { ThinkingEntry } from "./ThinkingPanel";

function preview(value: unknown, max = 240): string {
  if (value === undefined || value === null) return "";
  let str: string;
  if (typeof value === "string") str = value;
  else {
    try {
      str = JSON.stringify(value);
    } catch {
      str = String(value);
    }
  }
  return str.length > max ? `${str.slice(0, max)}…` : str;
}

export function eventToThinkingEntry(
  kind: string,
  data: Record<string, unknown>,
  timestamp: number,
  id: string,
): ThinkingEntry | null {
  switch (kind) {
    case "status": {
      const d = data as { stage?: string; sub_agents?: string[] };
      return {
        id,
        kind: "stage",
        label: d.stage ?? "stage",
        detail: d.sub_agents?.length ? `→ ${d.sub_agents.join(", ")}` : undefined,
        timestamp,
      };
    }
    case "decomposition": {
      const d = data as {
        rationale?: string;
        assignments?:
          | Record<string, string>
          | Array<{ sub_agent?: string; sub_task?: string }>;
        answer_mode?: string;
      };
      const assignmentsBody = (() => {
        if (!d.assignments) return undefined;
        if (Array.isArray(d.assignments)) {
          return d.assignments
            .map((a) => `• ${a.sub_agent ?? "?"}: ${a.sub_task ?? ""}`)
            .join("\n");
        }
        return Object.entries(d.assignments)
          .map(([k, v]) => `• ${k}: ${v}`)
          .join("\n");
      })();
      return {
        id,
        kind: "decomposition",
        label: "Decomposition plan",
        detail: d.answer_mode ? `mode: ${d.answer_mode}` : undefined,
        body: [d.rationale, assignmentsBody].filter(Boolean).join("\n\n"),
        timestamp,
      };
    }
    case "subagent_start": {
      const d = data as { name?: string; task?: string };
      return {
        id,
        kind: "subagent_start",
        label: `${d.name ?? "agent"} starts`,
        body: d.task,
        timestamp,
      };
    }
    case "subagent_done": {
      const d = data as {
        name?: string;
        skipped?: boolean;
        citations?: number;
        warnings?: string[];
      };
      const bits = [
        `${d.citations ?? 0} citations`,
        d.skipped ? "skipped" : null,
        d.warnings?.length ? `${d.warnings.length} warnings` : null,
      ].filter(Boolean);
      return {
        id,
        kind: "subagent_done",
        label: `${d.name ?? "agent"} done`,
        detail: bits.join(" · "),
        body: d.warnings?.length ? d.warnings.join("\n") : undefined,
        timestamp,
      };
    }
    case "tool_call": {
      const d = data as {
        sub_agent?: string;
        tool: string;
        input?: Record<string, unknown>;
      };
      return {
        id,
        kind: "tool_call",
        label: `${d.sub_agent ?? "lead"} → ${d.tool}`,
        body: d.input ? preview(d.input, 320) : undefined,
        timestamp,
      };
    }
    case "tool_result": {
      const d = data as {
        tool: string;
        record_count?: number;
        error?: string | null;
      };
      return {
        id,
        kind: "tool_result",
        label: `${d.tool} ${d.error ? "✗" : "✓"}`,
        detail: d.error ?? `${d.record_count ?? 0} records`,
        timestamp,
      };
    }
    case "critic": {
      const d = data as {
        grounding_score?: number;
        recommendation?: string;
        notes?: string;
      };
      return {
        id,
        kind: "critic",
        label: `Critic: ${d.recommendation ?? "review"}`,
        detail:
          d.grounding_score !== undefined
            ? `grounding ${d.grounding_score.toFixed(2)}`
            : undefined,
        body: d.notes,
        timestamp,
      };
    }
    case "warning": {
      const d = data as { message?: string };
      return {
        id,
        kind: "warning",
        label: d.message ?? "warning",
        timestamp,
      };
    }
    case "error": {
      const d = data as { message?: string };
      return {
        id,
        kind: "error",
        label: d.message ?? "error",
        timestamp,
      };
    }
    case "subagent_spawned": {
      const d = data as {
        parent?: string;
        name?: string;
        sub_task?: string;
        depth?: number;
      };
      const depth = typeof d.depth === "number" ? d.depth : 1;
      return {
        id,
        kind: "subagent_spawned",
        label: `${d.parent ?? "agent"} → spawn ${d.name ?? "?"}`,
        detail: depth ? `depth ${depth}` : undefined,
        body: d.sub_task,
        timestamp,
      };
    }
    case "subagent_completed": {
      const d = data as { node_id?: string; actual_tool_calls?: number };
      return {
        id,
        kind: "subagent_completed",
        label: "spawn completed",
        detail: d.actual_tool_calls != null ? `${d.actual_tool_calls} tool calls` : undefined,
        timestamp,
      };
    }
    case "spawn_refused": {
      const d = data as {
        parent?: string;
        name?: string;
        reason?: string;
        detail?: string;
      };
      return {
        id,
        kind: "spawn_refused",
        label: `${d.parent ?? "agent"} → spawn refused`,
        detail: [d.name, d.reason].filter(Boolean).join(" · "),
        body: d.detail,
        timestamp,
      };
    }
    case "route_classified": {
      const d = data as {
        route_class?: string;
        confidence?: number;
        rationale?: string;
      };
      return {
        id,
        kind: "stage",
        label: `Route: ${d.route_class ?? "unknown"}`,
        detail:
          d.confidence !== undefined ? `confidence ${d.confidence.toFixed(2)}` : undefined,
        body: d.rationale,
        timestamp,
      };
    }
    case "refine_start":
      return { id, kind: "stage", label: "Refine pass started", timestamp };
    case "cap_warning": {
      // v2 Step 7: warn the user when month-to-date is at 80% of cap.
      const d = data as { used_usd?: string; cap_usd?: string; ratio?: number };
      const pct = d.ratio ? Math.round(d.ratio * 100) : 80;
      return {
        id,
        kind: "stage",
        label: `Spend warning: ${pct}% of monthly cap used ($${d.used_usd ?? "?"} of $${d.cap_usd ?? "?"})`,
        timestamp,
      };
    }
    case "draft": {
      // The answer text itself is applied by the caller; this is just the
      // thinking-panel row.
      const d = data as { summary?: string };
      if (!d.summary) return null;
      return {
        id,
        kind: "draft",
        label: "First draft synthesised",
        detail: `${d.summary.length} chars`,
        timestamp,
      };
    }
    default:
      return null;
  }
}
